import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { Building2, Plus, Edit, Trash2, Save, X, Users } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyDepartment {
  id: string;
  name: string;
  description?: string;
  parent_department_id?: string;
  manager_id?: string;
}

interface UserDepartment {
  id: string;
  user_company_id: string;
  department_id: string;
  role_in_department?: string;
}

interface CompanyDepartmentsManagerProps {
  companyId: string;
}

const CompanyDepartmentsManager: React.FC<CompanyDepartmentsManagerProps> = ({
  companyId,
}) => {
  const { t } = useI18n();
  const [departments, setDepartments] = useState<CompanyDepartment[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    parent_department_id: '',
    manager_id: '',
  });

  const loadDepartments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.get(`/companies/${companyId}/departments`);
      setDepartments(response.data || []);
    } catch (err: any) {
      console.error('[CompanyDepartmentsManager] Failed to load departments', err);
      setError('Não foi possível carregar departamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDepartments();
  }, [companyId]);

  const resetForm = () => {
    setForm({ name: '', description: '', parent_department_id: '', manager_id: '' });
    setEditingId(null);
    setError(null);
    setMessage(null);
  };

  const handleEdit = (department: CompanyDepartment) => {
    setForm({
      name: department.name,
      description: department.description || '',
      parent_department_id: department.parent_department_id || '',
      manager_id: department.manager_id || '',
    });
    setEditingId(department.id);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const payload: any = {
        name: form.name,
        description: form.description || undefined,
      };

      if (form.parent_department_id) {
        payload.parent_department_id = form.parent_department_id;
      }

      if (form.manager_id) {
        payload.manager_id = form.manager_id;
      }

      if (editingId === 'new') {
        await authAPI.post(`/admin/companies/${companyId}/departments`, payload);
      } else {
        await authAPI.put(`/admin/companies/${companyId}/departments/${editingId}`, payload);
      }

      setMessage('Departamento salvo com sucesso!');
      resetForm();
      await loadDepartments();
    } catch (err: any) {
      console.error('[CompanyDepartmentsManager] Failed to save department', err);
      setError(err.response?.data?.error || 'Não foi possível salvar departamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (departmentId: string) => {
    if (!confirm('Tem certeza que deseja excluir este departamento?')) {
      return;
    }

    try {
      await authAPI.delete(`/admin/companies/${companyId}/departments/${departmentId}`);
      setMessage('Departamento excluído com sucesso!');
      await loadDepartments();
    } catch (err: any) {
      console.error('[CompanyDepartmentsManager] Failed to delete department', err);
      setError(err.response?.data?.error || 'Não foi possível excluir departamento.');
    }
  };

  const getParentName = (parentId?: string) => {
    if (!parentId) return '-';
    const parent = departments.find((d) => d.id === parentId);
    return parent ? parent.name : '-';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{t('rbac.departments.title')}</h3>
        {!editingId && (
          <button
            onClick={() => setEditingId('new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <Plus size={16} />
            Novo Departamento
          </button>
        )}
      </div>

      {error && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {message && (
        <div
          style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '6px',
            fontSize: '14px',
          }}
        >
          {message}
        </div>
      )}

      {editingId && (
        <form onSubmit={handleSave} style={{ marginBottom: '24px', padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          <h4 style={{ marginTop: 0 }}>{editingId === 'new' ? 'Novo Departamento' : 'Editar Departamento'}</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
                Nome
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
                placeholder="Vendas, Suporte, etc"
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>{t('rbac.departments.parentOptional')}</label>
              <select
                value={form.parent_department_id}
                onChange={(e) => setForm({ ...form, parent_department_id: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                }}
              >
                <option value="">{t('common.none')}</option>
                {departments
                  .filter((d) => d.id !== editingId || editingId === 'new')
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500' }}>
              Descrição
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              placeholder={t('rbac.departments.descPlaceholder')}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <Save size={16} />
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#6b7280',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <X size={16} />{t('actions.cancel')}</button>
          </div>
        </form>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.name')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('common.description')}</th>
              <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>{t('rbac.departments.parent')}</th>
              <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {departments.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>{t('rbac.departments.noneConfigured')}</td>
              </tr>
            ) : (
              departments.map((dept) => (
                <tr key={dept.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{dept.name}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>{dept.description || '-'}</td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {getParentName(dept.parent_department_id)}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleEdit(dept)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        title={t('actions.edit')}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(dept.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#fee2e2',
                          border: '1px solid #fecaca',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyDepartmentsManager;

